import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, getDocs, doc, getDoc, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Offer, Listing } from '../types';
import { useNavigate } from 'react-router-dom';
import OrphanOfferManager from './OrphanOfferManager';
import OfferCompareModal from './OfferCompareModal';
import { calculatePtcScore, buildPtcInputFromOffer } from '../services/ptcCalculator';
import './OffersTable.css';

const OffersTable: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [listings, setListings] = useState<{ [key: string]: Listing }>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'active' | 'sold'>('all');
  const [showOrphans, setShowOrphans] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [filterLender, setFilterLender] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showOrphanManager, setShowOrphanManager] = useState(false);
  const [allListings, setAllListings] = useState<Listing[]>([]);
  const [debugOffer, setDebugOffer] = useState<Offer | null>(null);
  const [selectedOfferIds, setSelectedOfferIds] = useState<Set<string>>(new Set());
  const [showCompare, setShowCompare] = useState(false);
  const itemsPerPage = 20;
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch offers
        const offersQuery = query(
          collection(db, 'offers'),
          where('userId', '==', currentUser.uid)
        );
        const offersSnapshot = await getDocs(offersQuery);
        const offersData: Offer[] = [];
        const listingIds = new Set<string>();

        offersSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          
          // Only include offers with completed parsing status
          if (data.parsingStatus !== 'completed') {
            return;
          }
          
          const offer = {
            id: docSnap.id,
            ...data,
            submissionDate: data.submissionDate?.toDate()
          } as Offer;
          offersData.push(offer);
          if (offer.listingId) {
            listingIds.add(offer.listingId);
          }
        });

        // Sort by submission date desc
        offersData.sort((a, b) => b.submissionDate.getTime() - a.submissionDate.getTime());

        // Backfill: recompute live scores and save any that differ from what's stored.
        // This silently reconciles stale scores (e.g. calculated with the old algorithm).
        const staleOffers = offersData.filter(o => {
          if (!o.id || !o.extractedData) return false;
          const liveInput = buildPtcInputFromOffer(o.extractedData, o.lenderExperienceYears ?? undefined);
          const liveScore = Math.round(calculatePtcScore(liveInput).ptcScore);
          return typeof o.ptcScore !== 'number' || Math.abs(Math.round(o.ptcScore) - liveScore) > 0;
        });
        if (staleOffers.length > 0) {
          // Firestore batch limit is 500; all offers per user are well within that
          const batch = writeBatch(db);
          staleOffers.forEach(o => {
            const liveInput = buildPtcInputFromOffer(o.extractedData!, o.lenderExperienceYears ?? undefined);
            const result = calculatePtcScore(liveInput);
            const offerRef = doc(db, 'offers', o.id!);
            batch.update(offerRef, {
              ptcScore: result.ptcScore,
              ptcBreakdown: result.ptcBreakdown,
              ptcCalculatedAt: Timestamp.now(),
            });
            // Update in-memory so UI reflects the corrected score immediately
            o.ptcScore = result.ptcScore;
            o.ptcBreakdown = result.ptcBreakdown;
          });
          batch.commit().catch(err => console.warn('Score backfill failed:', err));
        }

        setOffers(offersData);

        // Fetch listings
        const listingsMap: { [key: string]: Listing } = {};
        for (const listingId of Array.from(listingIds)) {
          const listingDoc = await getDoc(doc(db, 'listings', listingId));
          if (listingDoc.exists()) {
            listingsMap[listingId] = { id: listingDoc.id, ...listingDoc.data() } as Listing;
          }
        }
        setListings(listingsMap);

        // Fetch ALL user's listings (for orphan offer manager)
        const allListingsQuery = query(
          collection(db, 'listings'),
          where('userId', '==', currentUser.uid)
        );
        const allListingsSnapshot = await getDocs(allListingsQuery);
        const allListingsArray: Listing[] = [];
        allListingsSnapshot.forEach((doc) => {
          allListingsArray.push({ id: doc.id, ...doc.data() } as Listing);
        });
        setAllListings(allListingsArray);
      } catch (error) {
        console.error('Error fetching offers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  useEffect(() => {
    let filtered = offers;
    
    // Filter by orphan status first
    if (showOrphans) {
      filtered = filtered.filter(offer => offer.status === 'pending_listing_attachment');
    } else {
      filtered = filtered.filter(offer => offer.status !== 'pending_listing_attachment');
    }
    
    // Apply status filter (only for non-orphan offers)
    if (!showOrphans && filter !== 'all') {
      filtered = filtered.filter(offer => {
        const listing = offer.listingId ? listings[offer.listingId] : null;
        return listing?.status === filter;
      });
    }
    
    // Apply date filter
    if (filterDate) {
      filtered = filtered.filter(offer => {
        if (!offer.submissionDate) return false;
        const offerDate = new Date(offer.submissionDate).toISOString().split('T')[0];
        return offerDate === filterDate;
      });
    }
    
    // Apply property filter
    if (filterProperty) {
      filtered = filtered.filter(offer => {
        const listing = offer.listingId ? listings[offer.listingId] : null;
        if (!listing) return false;
        const searchTerm = filterProperty.toLowerCase();
        return listing.address?.toLowerCase().includes(searchTerm) ||
               listing.city?.toLowerCase().includes(searchTerm) ||
               listing.zipCode?.includes(searchTerm);
      });
    }
    
    // Apply agent filter
    if (filterAgent) {
      filtered = filtered.filter(offer => {
        const agentName = offer.extractedData?.agents?.buyer_broker?.agent_name
          || offer.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentName
          || offer.combinedOfferData?.purchaseAgreement?.buyer?.agentName;
        if (!agentName) return false;
        return agentName.toLowerCase().includes(filterAgent.toLowerCase());
      });
    }

    // Apply lender filter
    if (filterLender) {
      filtered = filtered.filter(offer => {
        const lenderName = offer.extractedData?.pre_approval?.lender_name
          || offer.combinedOfferData?.preApproval?.lender?.name;
        if (!lenderName) return false;
        return lenderName.toLowerCase().includes(filterLender.toLowerCase());
      });
    }
    
    setFilteredOffers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filter, offers, listings, filterDate, filterProperty, filterAgent, filterLender, showOrphans]);

  // Group filtered offers by listing (preserves existing pagination — groups paginate together)
  const offerGroups = useMemo(() => {
    const map = new Map<string, { listing: typeof listings[string] | null; offers: Offer[] }>();
    filteredOffers.forEach(offer => {
      const key = offer.listingId || '__no_listing__';
      if (!map.has(key)) {
        map.set(key, { listing: offer.listingId ? listings[offer.listingId] : null, offers: [] });
      }
      map.get(key)!.offers.push(offer);
    });
    // Sort each group's offers by submission date descending
    map.forEach(g => g.offers.sort((a, b) => b.submissionDate.getTime() - a.submissionDate.getTime()));
    // Sort groups: named listings alphabetically, no-listing group last
    type Group = { key: string; listing: typeof listings[string] | null; offers: Offer[] };
    const entries: Group[] = [];
    map.forEach((g, key) => entries.push({ key, ...g }));
    return entries
      .sort((a: Group, b: Group) => {
        if (a.key === '__no_listing__') return 1;
        if (b.key === '__no_listing__') return -1;
        return (a.listing?.address || '').localeCompare(b.listing?.address || '');
      })
      .map(({ listing, offers }) => ({ listing, offers }));
  }, [filteredOffers, listings]);

  // Paginate by group so a listing's offers are never split across pages
  const totalPages = Math.ceil(offerGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedGroups = offerGroups.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getScoreClass = (score: number) => {
    if (score >= 70) return 'excellent';
    if (score >= 50) return 'good';
    if (score >= 30) return 'fair';
    return 'poor';
  };

  // Compute the correct live PTC score for any offer using the same method as
  // EditModal — reads extractedData + cached lenderExperienceYears (pure JS, no network).
  // Falls back to stored ptcScore only when extractedData is absent.
  const getLiveScore = (offer: Offer): number | null => {
    if (offer.extractedData) {
      const input = buildPtcInputFromOffer(
        offer.extractedData,
        offer.lenderExperienceYears ?? undefined
      );
      return Math.round(calculatePtcScore(input).ptcScore);
    }
    return typeof offer.ptcScore === 'number' ? Math.round(offer.ptcScore) : null;
  };

  // ── DEBUG: live recalculation for the selected offer ──
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debugLiveResult = useMemo(() => {
    if (!debugOffer?.extractedData) return null;
    try {
      const input = buildPtcInputFromOffer(
        debugOffer.extractedData,
        (debugOffer as any).lenderExperienceYears ?? undefined
      );
      const result = calculatePtcScore(input);
      return { input, result };
    } catch (e: any) {
      return { error: e?.message || String(e) };
    }
  }, [debugOffer]);

  if (loading) {
    return (
      <div className="offers-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading offers...</p>
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="offers-table-container">
        <div className="offers-header">
          <h2>My Offers</h2>
          <p>All submitted offers in one place</p>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Offers Yet</h3>
          <p>Submit your first offer to get started</p>
          <button className="primary-button" onClick={() => navigate('/')}>
            Submit an Offer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="offers-table-container">
      <div className="offers-sticky-header">
      <div className="offers-header">
        <div>
          <h2>My Offers</h2>
          <p>{filteredOffers.length} {filteredOffers.length === 1 ? 'offer' : 'offers'} {filter !== 'all' ? `(${filter})` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {selectedOfferIds.size >= 2 && (
            <button
              className="compare-trigger-btn"
              onClick={() => setShowCompare(true)}
              title={selectedOfferIds.size > 4 ? 'Select up to 4 offers to compare' : undefined}
              disabled={selectedOfferIds.size > 4}
            >
              Compare ({selectedOfferIds.size})
            </button>
          )}
          {selectedOfferIds.size === 1 && (
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Select 1 more to compare</span>
          )}
          <button
            className="submit-offer-button"
            onClick={() => navigate('/submit-offer')}
          >
            + Submit Offer
          </button>
        </div>
      </div>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' && !showOrphans ? 'active' : ''}`}
          onClick={() => { setFilter('all'); setShowOrphans(false); }}
        >
          All ({offers.filter(o => o.status !== 'pending_listing_attachment').length})
        </button>
        <button
          className={`filter-tab ${filter === 'pending' && !showOrphans ? 'active' : ''}`}
          onClick={() => { setFilter('pending'); setShowOrphans(false); }}
        >
          Pending ({offers.filter(o => o.status !== 'pending_listing_attachment' && listings[o.listingId || '']?.status === 'pending').length})
        </button>
        <button
          className={`filter-tab ${filter === 'active' && !showOrphans ? 'active' : ''}`}
          onClick={() => { setFilter('active'); setShowOrphans(false); }}
        >
          Active ({offers.filter(o => o.status !== 'pending_listing_attachment' && listings[o.listingId || '']?.status === 'active').length})
        </button>
        <button
          className={`filter-tab ${filter === 'sold' && !showOrphans ? 'active' : ''}`}
          onClick={() => { setFilter('sold'); setShowOrphans(false); }}
        >
          Sold ({offers.filter(o => o.status !== 'pending_listing_attachment' && listings[o.listingId || '']?.status === 'sold').length})
        </button>
        <button
          className={`filter-tab orphan-tab ${showOrphans ? 'active' : ''}`}
          onClick={() => setShowOrphans(!showOrphans)}
          title="Offers without a listing"
        >
          📎 Offers w/o Listing
          {offers.filter(o => o.status === 'pending_listing_attachment').length > 0 && (
            <span className="orphan-count">
              {offers.filter(o => o.status === 'pending_listing_attachment').length}
            </span>
          )}
        </button>
      </div>

      {/* Orphan Management Section */}
      {offers.filter(o => o.status === 'pending_listing_attachment').length > 0 && (
        <div className="orphan-management-section" style={{ marginBottom: '20px', padding: '16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: '#92400e' }}>
              🔗 {offers.filter(o => o.status === 'pending_listing_attachment').length} Orphan Offer(s) Waiting
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#b45309' }}>
              Connect these offers to listings for better organization
            </p>
          </div>
          <button
            onClick={() => setShowOrphanManager(true)}
            style={{
              padding: '10px 16px',
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginLeft: '16px'
            }}
          >
            Manage Orphans
          </button>
        </div>
      )}

      {/* Search Filters */}
      <div className="filters-section" style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
        <div style={{ flex: '1', minWidth: '180px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Date</label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        <div style={{ flex: '1', minWidth: '180px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Property</label>
          <input
            type="text"
            placeholder="Search address..."
            value={filterProperty}
            onChange={(e) => setFilterProperty(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        <div style={{ flex: '1', minWidth: '180px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Agent</label>
          <input
            type="text"
            placeholder="Search agent name..."
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        <div style={{ flex: '1', minWidth: '180px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Lender</label>
          <input
            type="text"
            placeholder="Search lender name..."
            value={filterLender}
            onChange={(e) => setFilterLender(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        {(filterDate || filterProperty || filterAgent || filterLender) && (
          <button
            onClick={() => {
              setFilterDate('');
              setFilterProperty('');
              setFilterAgent('');
              setFilterLender('');
            }}
            style={{ alignSelf: 'flex-end', padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Clear Filters
          </button>
        )}
      </div>
      </div>{/* end offers-sticky-header */}

      <div className="table-wrapper">
        <table className="offers-table">
          <thead>
            <tr>
              <th style={{ width: '36px', padding: '12px 8px 12px 14px' }}></th>
              <th style={{ width: '100px' }}>Date</th>
              <th style={{ width: '220px' }}>Property</th>
              <th style={{ width: '160px' }}>Agent</th>
              <th style={{ width: '160px' }}>Lender</th>
              <th style={{ width: '120px' }}>Loan Type</th>
              <th style={{ width: '130px' }}>Purchase</th>
              <th style={{ width: '130px' }}>Loan Amount</th>
              <th style={{ width: '90px' }}>PTC Score</th>
              <th style={{ width: '110px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedGroups.map((group) => {
              const { listing, offers: groupOffers } = group;
              const groupKey = listing?.id || '__no_listing__';
              return (
                <React.Fragment key={groupKey}>
                  {/* ── Property group header ── */}
                  {(() => {
                    const groupIds = groupOffers.map(o => o.id!);
                    const selectedCount = groupIds.filter(id => selectedOfferIds.has(id)).length;
                    const allSelected = selectedCount === groupIds.length && groupIds.length > 0;
                    const someSelected = selectedCount > 0 && !allSelected;
                    return (
                      <tr className="offers-group-header-row">
                        <td colSpan={10}>
                          <div className="offers-group-header-content">
                            <div className="offers-group-left">
                              <input
                                type="checkbox"
                                className="offers-group-checkbox"
                                title={allSelected ? 'Deselect all in this listing' : 'Select all in this listing'}
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected; }}
                                onChange={e => {
                                  setSelectedOfferIds(prev => {
                                    const next = new Set(prev);
                                    groupIds.forEach(id => e.target.checked ? next.add(id) : next.delete(id));
                                    return next;
                                  });
                                }}
                              />
                              <span className="offers-group-address">
                                {listing ? `${listing.address}${listing.city ? `, ${listing.city}` : ''}${listing.state ? ` ${listing.state}` : ''}` : 'Offers without a Listing'}
                              </span>
                              {listing && (
                                <div className="offers-group-chips">
                                  {listing.mlsNumber && (
                                    <span className="og-chip og-chip-mls">MLS# {listing.mlsNumber}</span>
                                  )}
                                  {listing.listingPrice && (
                                    <span className="og-chip og-chip-lp">LP {formatCurrency(listing.listingPrice)}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="offers-group-count">
                              {someSelected || allSelected ? `${selectedCount}/` : ''}{groupOffers.length} offer{groupOffers.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })()}

                  {/* ── Offer rows for this group ── */}
                  {groupOffers.map((offer) => {
                    const preApproval = offer.extractedData?.pre_approval;
                    const lenderName = preApproval?.lender_name || offer.combinedOfferData?.preApproval?.lender?.name;
                    const lenderNmls = preApproval?.loan_officer_nmls || offer.combinedOfferData?.preApproval?.lender?.nmlsId;
                    const loanType = offer.extractedData?.financing?.mortgage_type || offer.combinedOfferData?.preApproval?.loan?.loanType;
                    const purchasePrice = offer.extractedData?.transaction?.final_sales_price ?? offer.combinedOfferData?.preApproval?.loan?.purchasePrice ?? offer.combinedOfferData?.purchaseAgreement?.financial?.purchasePrice;
                    const loanAmount = offer.extractedData?.financing?.mortgage_amount ?? offer.combinedOfferData?.preApproval?.loan?.loanAmount ?? offer.combinedOfferData?.purchaseAgreement?.financial?.loanAmount;
                    const agentName = offer.extractedData?.agents?.buyer_broker?.agent_name || offer.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentName || offer.combinedOfferData?.purchaseAgreement?.buyer?.agentName;
                    const agentId = offer.extractedData?.agents?.buyer_broker?.agent_id || offer.combinedOfferData?.purchaseAgreement?.buyerAgent?.agentId || offer.combinedOfferData?.purchaseAgreement?.buyer?.agentId;

                    return (
                      <tr key={offer.id} className={`${offer.status === 'pending_listing_attachment' ? 'orphan-row' : ''}${selectedOfferIds.has(offer.id!) ? ' offer-row-selected' : ''}`}>
                        <td style={{ padding: '12px 8px 12px 14px' }}>
                          <input
                            type="checkbox"
                            checked={selectedOfferIds.has(offer.id!)}
                            onChange={e => {
                              setSelectedOfferIds(prev => {
                                const next = new Set(prev);
                                e.target.checked ? next.add(offer.id!) : next.delete(offer.id!);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td><span className="date-text">{formatDate(offer.submissionDate)}</span></td>
                        <td>
                          {offer.status === 'pending_listing_attachment' ? (
                            <div className="orphan-indicator">
                              <span className="orphan-icon">📎</span>
                              <div>
                                <div className="orphan-label">Orphan Offer</div>
                                <div className="orphan-reason">{offer.orphanReason || 'No listing attached'}</div>
                              </div>
                            </div>
                          ) : listing ? (
                            <span className="text-muted" style={{ fontSize: '12px' }}>↳ {listing.city}, {listing.state}</span>
                          ) : (
                            <span className="text-muted">No listing</span>
                          )}
                        </td>
                        <td>
                          {agentName ? (
                            <div>
                              <div className="agent-name">{agentName}</div>
                              {agentId && <div className="agent-id-text">Lic# {agentId}</div>}
                            </div>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>
                          <div>
                            <div className="lender-name">{lenderName || <span className="text-muted">Pending</span>}</div>
                            {lenderNmls && <div className="nmls-text">NMLS# {lenderNmls}</div>}
                          </div>
                        </td>
                        <td>{loanType || <span className="text-muted">Pending</span>}</td>
                        <td className="amount-text">
                          {purchasePrice ? formatCurrency(purchasePrice) : '-'}
                          {(offer.extractedData?.escalation_clause?.present || offer.extractedData?.appraisal_guarantee?.present) && (
                            <div className="price-clause-badges">
                              {offer.extractedData?.escalation_clause?.present && (
                                <span className="price-clause-badge price-clause-escalation">⚡ Escalation</span>
                              )}
                              {offer.extractedData?.appraisal_guarantee?.present && (
                                <span className="price-clause-badge price-clause-appraisal">🛡️ Gap</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="amount-text">{loanAmount ? formatCurrency(loanAmount) : '-'}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {(() => { const s = getLiveScore(offer); return s !== null ? (
                              <span className={`score-badge ${getScoreClass(s)}`}>{s}</span>
                            ) : (
                              <span className="score-pending">—</span>
                            ); })()}
                            <button
                              title="Debug PTC Score"
                              onClick={() => setDebugOffer(offer)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '2px', lineHeight: 1, color: '#6b7280' }}
                            >🔍</button>
                          </div>
                        </td>
                        <td>
                          <button
                            className="view-btn"
                            onClick={() => navigate(`/offers/${offer.id}`, { state: { listing: offer.listingId ? listings[offer.listingId] : null } })}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '15px',
          marginTop: '20px',
          padding: '15px'
        }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              background: currentPage === 1 ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>
            Page {currentPage} of {totalPages} ({filteredOffers.length} offer{filteredOffers.length !== 1 ? 's' : ''})
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage >= totalPages}
            style={{
              padding: '8px 16px',
              background: currentPage >= totalPages ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* ── DEBUG: PTC Score Inspector (remove before prod) ── */}
      {debugOffer && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={() => setDebugOffer(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#1e1b4b', borderRadius: '10px 10px 0 0' }}>
              <div>
                <div style={{ color: '#a5b4fc', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>🔍 PTC Score Debug — Internal Use Only</div>
                <div style={{ color: '#fff', fontSize: '13px', marginTop: '2px', fontFamily: 'monospace' }}>{debugOffer.id}</div>
              </div>
              <button onClick={() => setDebugOffer(null)} style={{ background: 'none', border: 'none', color: '#a5b4fc', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Scores side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px' }}>
                  <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700, textTransform: 'uppercase' }}>Stored Score</div>
                  <div style={{ fontSize: '36px', fontWeight: 800, color: '#15803d', lineHeight: 1.2 }}>
                    {typeof debugOffer.ptcScore === 'number' ? Math.round(debugOffer.ptcScore) : '—'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Saved on offer document</div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 16px' }}>
                  <div style={{ fontSize: '11px', color: '#2563eb', fontWeight: 700, textTransform: 'uppercase' }}>Live Recalc</div>
                  <div style={{ fontSize: '36px', fontWeight: 800, color: '#1d4ed8', lineHeight: 1.2 }}>
                    {debugLiveResult && 'result' in debugLiveResult ? Math.round((debugLiveResult as any).result.ptcScore) : '—'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Current algorithm</div>
                  {'error' in (debugLiveResult ?? {}) && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>{(debugLiveResult as any).error}</div>
                  )}
                </div>
              </div>

              {/* Live breakdown */}
              {debugLiveResult && 'result' in debugLiveResult && (() => {
                const liveResult = (debugLiveResult as { result: ReturnType<typeof calculatePtcScore>; input: ReturnType<typeof buildPtcInputFromOffer> }).result;
                const bd = liveResult.ptcBreakdown;
                return (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Live Calculation Steps</div>
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <tbody>
                          <DebugRow label="Cash Offer?" value={bd.isCashOffer ? `Yes → Score = ${liveResult.ptcScore}` : 'No'} />
                          <DebugRow label="Has Pre-Approval?" value={bd.hasPreApproval ? 'Yes' : 'No → Score = 40 (flat)'} highlight={!bd.hasPreApproval} />
                          {!bd.isCashOffer && bd.hasPreApproval && <>
                            <DebugRow label={`Lender Experience (${bd.lenderExperienceYears} yrs)`} value={`+${bd.lenderExperiencePoints} pts`} positive />
                            <DebugRow label={`LTV ${bd.ltvPercent !== null ? bd.ltvPercent!.toFixed(1) + '%' : 'unknown'} — ${bd.loanType}`} value={bd.ltvPoints > 0 ? `+${bd.ltvPoints} pts` : '+0 pts (LTV data missing?)'} positive={bd.ltvPoints > 0} highlight={bd.ltvPoints === 0} />
                            {bd.appraisalContingencyDeduction > 0 && <DebugRow label="Appraisal Contingency" value={`−${bd.appraisalContingencyDeduction} pts`} negative />}
                            {bd.homeInspectionContingencyDeduction > 0 && <DebugRow label="Home Inspection Contingency" value={`−${bd.homeInspectionContingencyDeduction} pts`} negative />}
                            {bd.saleOfBuyerPropertyContingencyDeduction > 0 && <DebugRow label="Sale of Buyer's Property" value={`−${bd.saleOfBuyerPropertyContingencyDeduction} pts`} negative />}
                          </>}
                          <tr style={{ borderTop: '2px solid #d1d5db', background: '#f0f9ff' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 700, color: '#1e40af' }}>Final Score</td>
                            <td style={{ padding: '8px 12px', fontWeight: 800, fontSize: '15px', color: '#1e40af', textAlign: 'right' }}>{Math.round(liveResult.ptcScore)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Stored breakdown */}
              {debugOffer.ptcBreakdown && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Stored ptcBreakdown (raw)</div>
                  <pre style={{ background: '#1f2937', color: '#d1fae5', fontSize: '11px', padding: '12px', borderRadius: '8px', overflow: 'auto', margin: 0, maxHeight: '160px' }}>
                    {JSON.stringify(debugOffer.ptcBreakdown, null, 2)}
                  </pre>
                </div>
              )}

              {/* Inputs used */}
              {debugLiveResult && 'input' in debugLiveResult && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Inputs Passed to Calculator</div>
                  <pre style={{ background: '#1f2937', color: '#fef3c7', fontSize: '11px', padding: '12px', borderRadius: '8px', overflow: 'auto', margin: 0, maxHeight: '160px' }}>
                    {JSON.stringify((debugLiveResult as any).input, null, 2)}
                  </pre>
                </div>
              )}

              {/* Raw extractedData fields */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Key extractedData Fields</div>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <tbody>
                      {[
                        ['_meta.has_pre_approval',                     debugOffer.extractedData?._meta?.has_pre_approval],
                        ['financing.payment_method',                   debugOffer.extractedData?.financing?.payment_method],
                        ['financing.mortgage_type',                    debugOffer.extractedData?.financing?.mortgage_type],
                        ['financing.mortgage_amount',                  debugOffer.extractedData?.financing?.mortgage_amount],
                        ['financing.down_payment_amount',              debugOffer.extractedData?.financing?.down_payment_amount],
                        ['transaction.final_sales_price',              debugOffer.extractedData?.transaction?.final_sales_price],
                        ['pre_approval.loan_type',                     debugOffer.extractedData?.pre_approval?.loan_type],
                        ['pre_approval.lender_name',                   debugOffer.extractedData?.pre_approval?.lender_name],
                        ['pre_approval.loan_officer_nmls',             debugOffer.extractedData?.pre_approval?.loan_officer_nmls],
                        ['pre_approval.base_loan_amount',              debugOffer.extractedData?.pre_approval?.base_loan_amount],
                        ['contingencies.appraisal',                    debugOffer.extractedData?.contingencies?.appraisal],
                        ['contingencies.inspection.waived',            debugOffer.extractedData?.contingencies?.inspection?.waived],
                        ['contingencies.sale_of_buyers_property',      debugOffer.extractedData?.contingencies?.sale_of_buyers_property],
                        ['offer.lenderExperienceYears (cached)',        (debugOffer as any).lenderExperienceYears],
                        ['_meta.extraction_confidence',                debugOffer.extractedData?._meta?.extraction_confidence],
                      ].map(([key, val]) => (
                        <tr key={String(key)} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '5px 12px', color: '#6b7280', fontFamily: 'monospace', fontSize: '11px' }}>{String(key)}</td>
                          <td style={{ padding: '5px 12px', fontWeight: 600, color: val === undefined || val === null ? '#d1d5db' : val === false ? '#dc2626' : val === true ? '#16a34a' : '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                            {val === undefined ? 'undefined' : val === null ? 'null' : String(val)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Offer Compare Modal */}
      {showCompare && selectedOfferIds.size >= 2 && (
        <OfferCompareModal
          offers={filteredOffers.filter(o => o.id && selectedOfferIds.has(o.id)).slice(0, 4)}
          listings={listings}
          onClose={() => setShowCompare(false)}
        />
      )}

      {/* Orphan Offer Manager Modal */}
      {showOrphanManager && (
        <OrphanOfferManager
          orphanOffers={offers.filter(o => o.status === 'pending_listing_attachment')}
          allListings={allListings}
          onClose={() => setShowOrphanManager(false)}
          onOfferLinked={(offerId, listingId) => {
            // Reload offers to reflect the change
            setOffers(prev =>
              prev.map(o =>
                o.id === offerId ? { ...o, listingId, status: 'pending' } : o
              )
            );
          }}
        />
      )}
    </div>
  );
};

// ── DEBUG helper (remove before prod) ──────────────────────────────────────
const DebugRow: React.FC<{
  label: string; value: string;
  positive?: boolean; negative?: boolean; highlight?: boolean;
}> = ({ label, value, positive, negative, highlight }) => (
  <tr style={{ borderBottom: '1px solid #f3f4f6', background: highlight ? '#fef9c3' : 'transparent' }}>
    <td style={{ padding: '6px 12px', color: '#374151' }}>{label}</td>
    <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600,
      color: positive ? '#16a34a' : negative ? '#dc2626' : highlight ? '#b45309' : '#111827' }}>
      {value}
    </td>
  </tr>
);

export default OffersTable;
